import { useEffect, useMemo, useState, useRef } from "react";
import { useCrm } from "@/lib/crm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ClipboardList, Upload, Download, FileSpreadsheet, ArrowRight, ArrowLeft, Trash2,
  AlertTriangle, MapPin, Users2, Filter, CheckCircle2, XCircle, Layers, FileText, Plus,
} from "lucide-react";

const CANALE_LBL = { privati: "Privato", partner: "Partner", experience: "Experience" };
const CANALE_CLS = {
  privati: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  partner: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  experience: "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
};

const STATO = {
  da_contattare: { label: "Da contattare", cls: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/60 dark:text-sky-300 dark:border-sky-800" },
  spostato: { label: "In Pipeline", cls: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800" },
  scartato: { label: "Scartato", cls: "bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700" },
  eliminato: { label: "Eliminata da Pipeline", cls: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800" },
};

export default function Liste() {
  const { api, collaboratori, refresh, activeSettore, progetti } = useCrm();
  const isRitr = activeSettore === "ritrattistica";
  const entLabel = isRitr ? "Clienti" : "Società";
  const [liste, setListe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState("all");
  const [canFilter, setCanFilter] = useState("all");

  const [selected, setSelected] = useState(null); // lista aperta
  const [contatti, setContatti] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [assign, setAssign] = useState("none");
  const [working, setWorking] = useState(false);
  const [expBusy, setExpBusy] = useState("");

  // aggiungi contatto
  const emptyContatto = { nome: "", disciplina: "", citta: "", referente: "", telefono: "", email: "", atleti: "", note: "", canale: "privati", ruolo: "", experience_nome: "", partner_nome: "" };
  const [addOpen, setAddOpen] = useState(false);
  const [nc, setNc] = useState(emptyContatto);
  const [savingC, setSavingC] = useState(false);

  // import dialog
  const [impOpen, setImpOpen] = useState(false);
  const [impNome, setImpNome] = useState("");
  const [impCat, setImpCat] = useState("");
  const [impNote, setImpNote] = useState("");
  const [impFile, setImpFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const loadListe = async () => {
    const l = await api.listListe();
    setListe(l);
    setLoading(false);
  };
  useEffect(() => { loadListe(); }, []); // eslint-disable-line

  const categories = useMemo(() => Array.from(new Set(liste.map((l) => l.categoria).filter(Boolean))), [liste]);
  const visibleListe = useMemo(
    () => (catFilter === "all" ? liste : liste.filter((l) => l.categoria === catFilter)),
    [liste, catFilter]
  );

  const openLista = async (l) => {
    setSelected(l);
    setChecked(new Set());
    setAssign("none");
    const c = await api.listContatti(l.id);
    setContatti(c);
  };

  const reloadContatti = async (lid) => {
    const c = await api.listContatti(lid);
    setContatti(c);
  };

  const doImport = async () => {
    if (!impFile) { toast.error("Seleziona un file Excel o CSV"); return; }
    if (!impNome.trim()) { toast.error("Dai un nome alla lista"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", impFile);
      fd.append("nome", impNome.trim());
      fd.append("categoria", impCat.trim());
      fd.append("note", impNote.trim());
      const res = await api.importLista(fd);
      toast.success(`Lista "${res.lista.nome}" importata: ${res.creati} contatti${res.duplicati ? ` · ${res.duplicati} già presenti` : ""}`);
      setImpOpen(false);
      setImpNome(""); setImpCat(""); setImpNote(""); setImpFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await loadListe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'import");
    } finally {
      setImporting(false);
    }
  };

  const toggle = (id) => setChecked((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const selectableIds = useMemo(() => contatti.filter((c) => c.stato !== "spostato").map((c) => c.id), [contatti]);
  const allChecked = selectableIds.length > 0 && selectableIds.every((id) => checked.has(id));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(selectableIds));

  const sposta = async () => {
    const ids = Array.from(checked);
    if (ids.length === 0) return;
    setWorking(true);
    try {
      const res = await api.spostaPipeline(ids, assign !== "none" ? assign : null);
      toast.success(`${res.creati} contatti spostati nella Pipeline (prima fase)${res.sponsor ? ` · ${res.sponsor} aggiunti come partner/sponsor di Experience` : ""}`);
      (res.avvisi || []).forEach((w) => toast.warning(w));
      setChecked(new Set());
      await Promise.all([reloadContatti(selected.id), loadListe(), refresh()]);
    } catch {
      toast.error("Errore nello spostamento");
    } finally {
      setWorking(false);
    }
  };

  const setStato = async (c, stato) => {
    await api.updateContatto(c.id, { stato });
    await Promise.all([reloadContatti(selected.id), loadListe()]);
  };

  const delContatto = async (c) => {
    await api.deleteContatto(c.id);
    await Promise.all([reloadContatti(selected.id), loadListe()]);
    toast.success("Contatto eliminato");
  };

  const delLista = async (l) => {
    await api.deleteLista(l.id);
    setSelected(null);
    await loadListe();
    toast.success("Lista eliminata");
  };

  const addContatto = async () => {
    if (!nc.nome.trim()) { toast.error("Il nome società è obbligatorio"); return; }
    setSavingC(true);
    try {
      await api.addContatto(selected.id, { ...nc, atleti: Number(nc.atleti) || 0, canale: isRitr ? nc.canale : "" });
      setAddOpen(false);
      setNc(emptyContatto);
      await Promise.all([reloadContatti(selected.id), loadListe()]);
      toast.success("Contatto aggiunto");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'aggiunta");
    } finally {
      setSavingC(false);
    }
  };

  const exportLista = async (kind) => {
    setExpBusy(kind);
    try {
      if (kind === "xlsx") await api.exportLista(selected.id, selected.nome);
      else await api.exportListaPdf(selected.id, selected.nome);
      toast.success(`Export ${kind === "xlsx" ? "Excel" : "PDF"} generato`);
    } catch {
      toast.error("Errore durante l'export");
    } finally {
      setExpBusy("");
    }
  };

  // ---------- DETTAGLIO LISTA ----------
  if (selected) {
    const nSel = checked.size;
    return (
      <div className="space-y-6">
        <button data-testid="liste-back-button" onClick={() => setSelected(null)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4" /> Tutte le liste
        </button>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
              <ClipboardList className="h-6 w-6 text-primary" /> {selected.nome}
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="gap-1"><Layers className="h-3 w-3" />{selected.categoria}</Badge>
              <span className="text-sm text-slate-500">{contatti.length} contatti</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setNc(emptyContatto); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-contatto-button" size="sm" className="rounded-full gap-1.5"><Plus className="h-4 w-4" /> Aggiungi contatto</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Aggiungi contatto a "{selected.nome}"</DialogTitle>
                  <DialogDescription>Inserisci una singola società nella lista, senza reimportare il file.</DialogDescription>
                </DialogHeader>
                <div className="grid sm:grid-cols-2 gap-3 py-2">
                  {isRitr && (
                    <div className="grid gap-1.5 sm:col-span-2">
                      <Label>Canale</Label>
                      <Select value={nc.canale} onValueChange={(v) => setNc((x) => ({ ...x, canale: v }))}>
                        <SelectTrigger data-testid="nc-canale-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="privati">Cliente privato</SelectItem>
                          <SelectItem value="partner">Partner</SelectItem>
                          <SelectItem value="experience">Experience (partecipante, ente, sponsor…)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid gap-1.5 sm:col-span-2">
                    <Label>{isRitr ? (nc.canale === "partner" ? "Nome attività" : "Nome") : "Società"} *</Label>
                    <Input data-testid="nc-nome-input" value={nc.nome} onChange={(e) => setNc((v) => ({ ...v, nome: e.target.value }))} placeholder="Es. Polisportiva Nuova ASD" />
                  </div>
                  {isRitr && nc.canale === "experience" ? (<>
                    <div className="grid gap-1.5">
                      <Label>Ruolo</Label>
                      <Select value={nc.ruolo || "Partecipante"} onValueChange={(v) => setNc((x) => ({ ...x, ruolo: v }))}>
                        <SelectTrigger data-testid="nc-ruolo-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{["Partecipante", "Ente / Associazione", "Convention", "Sponsor", "Location"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-1.5">
                      <Label>Experience di interesse</Label>
                      <Select value={nc.experience_nome || "none"} onValueChange={(v) => setNc((x) => ({ ...x, experience_nome: v === "none" ? "" : v }))}>
                        <SelectTrigger data-testid="nc-experience-select"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">Nessuna</SelectItem>{(progetti || []).map((p) => <SelectItem key={p.id} value={p.nome}>{p.nome}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </>) : (
                  <div className="grid gap-1.5">
                    <Label>{isRitr ? (nc.canale === "partner" ? "Tipologia" : "Servizio di interesse") : "Disciplina"}</Label>
                    <Input data-testid="nc-disciplina-input" value={nc.disciplina} onChange={(e) => setNc((v) => ({ ...v, disciplina: e.target.value }))} />
                  </div>
                  )}
                  <div className="grid gap-1.5">
                    <Label>Città</Label>
                    <Input data-testid="nc-citta-input" value={nc.citta} onChange={(e) => setNc((v) => ({ ...v, citta: e.target.value }))} />
                  </div>
                  <div className="grid gap-1.5">
                    <Label>Referente</Label>
                    <Input data-testid="nc-referente-input" value={nc.referente} onChange={(e) => setNc((v) => ({ ...v, referente: e.target.value }))} />
                  </div>
                  <div className="grid gap-1.5">
                    <Label>Telefono</Label>
                    <Input data-testid="nc-telefono-input" value={nc.telefono} onChange={(e) => setNc((v) => ({ ...v, telefono: e.target.value }))} />
                  </div>
                  <div className="grid gap-1.5">
                    <Label>Email</Label>
                    <Input data-testid="nc-email-input" value={nc.email} onChange={(e) => setNc((v) => ({ ...v, email: e.target.value }))} />
                  </div>
                  {!isRitr && (
                  <div className="grid gap-1.5">
                    <Label>Atleti</Label>
                    <Input data-testid="nc-atleti-input" type="number" min="0" value={nc.atleti} onChange={(e) => setNc((v) => ({ ...v, atleti: e.target.value }))} />
                  </div>
                  )}
                  <div className="grid gap-1.5 sm:col-span-2">
                    <Label>Note</Label>
                    <Textarea data-testid="nc-note-input" rows={2} value={nc.note} onChange={(e) => setNc((v) => ({ ...v, note: e.target.value }))} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" className="rounded-full" onClick={() => setAddOpen(false)}>Annulla</Button>
                  <Button data-testid="nc-save-button" className="rounded-full gap-1.5" onClick={addContatto} disabled={savingC}><Plus className="h-4 w-4" /> {savingC ? "Salvataggio…" : "Aggiungi"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button data-testid="export-lista-excel-button" variant="outline" size="sm" className="rounded-full gap-1.5" disabled={expBusy} onClick={() => exportLista("xlsx")}>
              <FileSpreadsheet className="h-4 w-4" /> {expBusy === "xlsx" ? "…" : "Excel"}
            </Button>
            <Button data-testid="export-lista-pdf-button" variant="outline" size="sm" className="rounded-full gap-1.5" disabled={expBusy} onClick={() => exportLista("pdf")}>
              <FileText className="h-4 w-4" /> {expBusy === "pdf" ? "…" : "PDF"}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="delete-lista-button" variant="outline" size="sm" className="rounded-full gap-1.5 text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /> Elimina lista</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminare la lista "{selected.nome}"?</AlertDialogTitle>
                  <AlertDialogDescription>Verranno rimossi tutti i {contatti.length} contatti della lista. Le SSD già spostate nella Pipeline restano.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction data-testid="delete-lista-confirm" onClick={() => delLista(selected)}>Elimina</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* barra azioni selezione */}
        {nSel > 0 && (
          <Card data-testid="liste-action-bar" className="p-4 flex items-center gap-3 flex-wrap border-primary/30 bg-primary/5">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{nSel} selezionati</span>
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              <Select value={assign} onValueChange={setAssign}>
                <SelectTrigger data-testid="liste-assign-collab" className="h-9 w-52"><SelectValue placeholder="Assegna a…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun collaboratore</SelectItem>
                  {collaboratori.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid="liste-sposta-button" onClick={sposta} disabled={working} className="rounded-full gap-1.5">
                <ArrowRight className="h-4 w-4" /> {working ? "Attendere…" : "Sposta in Pipeline"}
              </Button>
            </div>
          </Card>
        )}

        {isRitr && contatti.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="liste-canale-filter">
            {[["all", "Tutti"], ["privati", "Clienti Privati"], ["partner", "Partner"], ["experience", "Experience"]].map(([k, l]) => (
              <button key={k} data-testid={`liste-canale-${k}`} onClick={() => setCanFilter(k)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${canFilter === k ? "bg-primary text-white border-primary" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
                {l} <span className="opacity-70 tabular-nums">{k === "all" ? contatti.length : contatti.filter((c) => (c.canale || "privati") === k).length}</span>
              </button>
            ))}
          </div>
        )}

        <Card className="p-0 overflow-hidden">
          {contatti.length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-400">Nessun contatto in questa lista.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 text-left">
                    <th className="px-4 py-3 w-10"><Checkbox data-testid="liste-check-all" checked={allChecked} onCheckedChange={toggleAll} /></th>
                    <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider">{isRitr ? "Cliente" : "Società"}</th>
                    <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider">{isRitr ? "Servizio / Tipologia / Ruolo" : "Disciplina"}</th>
                    <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider">Referente</th>
                    <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider">Contatti</th>
                    <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider">Stato</th>
                    <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {contatti.filter((c) => !isRitr || canFilter === "all" || (c.canale || "privati") === canFilter).map((c) => {
                    const st = STATO[c.stato] || STATO.da_contattare;
                    const moved = c.stato === "spostato";
                    return (
                      <tr key={c.id} data-testid={`contatto-row-${c.id}`} className={`border-b border-slate-100 dark:border-slate-800 ${moved ? "opacity-60" : "hover:bg-slate-50 dark:hover:bg-slate-900/40"} transition-colors`}>
                        <td className="px-4 py-3">
                          <Checkbox data-testid={`contatto-check-${c.id}`} disabled={moved} checked={checked.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900 dark:text-white flex items-center gap-1.5">
                            {c.nome}
                            {c.duplicato && <span title="Già presente in Pipeline/Portfolio"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /></span>}
                          </div>
                          {isRitr && (
                            <div className="flex flex-wrap gap-1 my-0.5">
                              <span data-testid={`contatto-canale-${c.id}`} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${CANALE_CLS[c.canale || "privati"]}`}>{CANALE_LBL[c.canale || "privati"]}</span>
                              {c.experience_nome && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300">{c.experience_nome}</span>}
                              {c.partner_nome && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">via {c.partner_nome}</span>}
                              {c.collaboratore_nome && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">{c.collaboratore_nome}</span>}
                            </div>
                          )}
                          {(c.citta || c.atleti > 0) && (
                            <div className="text-[11px] text-slate-400 inline-flex items-center gap-1">
                              {c.citta && <><MapPin className="h-3 w-3" />{c.citta}</>}
                              {c.atleti > 0 && <span className="ml-2 inline-flex items-center gap-1"><Users2 className="h-3 w-3" />{c.atleti}</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{(isRitr && c.canale === "experience" ? c.ruolo : c.disciplina) || "—"}{isRitr && c.contributo > 0 && <div className="text-[11px] text-emerald-600">Contributo € {c.contributo}</div>}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{c.referente || "—"}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">
                          {c.telefono && <div>{c.telefono}</div>}
                          {c.email && <div className="text-slate-400">{c.email}</div>}
                          {!c.telefono && !c.email && "—"}
                        </td>
                        <td className="px-4 py-3">
                          <Badge data-testid={`contatto-stato-${c.id}`} variant="outline" className={st.cls}>{st.label}</Badge>
                          {c.stato === "eliminato" && c.eliminato_at && <div className="text-[10px] text-rose-500 mt-1">il {new Date(c.eliminato_at).toLocaleDateString("it-IT")}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {!moved && c.stato !== "scartato" && c.stato !== "eliminato" && (
                              <button data-testid={`contatto-scarta-${c.id}`} title="Segna come scartato" onClick={() => setStato(c, "scartato")} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><XCircle className="h-4 w-4" /></button>
                            )}
                            {(c.stato === "scartato" || c.stato === "eliminato") && (
                              <button data-testid={`contatto-ripristina-${c.id}`} title="Ripristina" onClick={() => setStato(c, "da_contattare")} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><CheckCircle2 className="h-4 w-4" /></button>
                            )}
                            <button data-testid={`contatto-delete-${c.id}`} title="Elimina" onClick={() => delContatto(c)} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-destructive hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    );
  }

  // ---------- ELENCO LISTE ----------
  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <ClipboardList className="h-7 w-7 text-primary" /> Liste Contatti
          </h1>
          <p className="text-slate-500 mt-1">Il tuo magazzino di società da contattare. Importa una lista, poi sposta i contatti nella Pipeline quando inizi a lavorarli.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="download-template-button" variant="outline" className="rounded-full gap-1.5" onClick={() => api.downloadTemplateContatti()}>
            <Download className="h-4 w-4" /> Modello Excel
          </Button>
          <Dialog open={impOpen} onOpenChange={setImpOpen}>
            <DialogTrigger asChild>
              <Button data-testid="import-lista-button" className="rounded-full gap-1.5"><Upload className="h-4 w-4" /> Importa lista</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Importa una lista di contatti</DialogTitle>
                <DialogDescription>Carica un file Excel (.xlsx) o CSV. {isRitr ? "Usa il modello Ritrattistica con 3 fogli (Clienti Privati, Partner, Experience): il canale si riconosce dal nome del foglio. Funzionano anche file con un solo foglio (colonna Canale facoltativa)." : "Colonne riconosciute: Società, Disciplina, Città, Referente, Telefono, Email, Atleti, Collaboratore assegnato, Note."}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-2">
                <div className="grid gap-1.5">
                  <Label>Nome lista *</Label>
                  <Input data-testid="import-nome-input" value={impNome} onChange={(e) => setImpNome(e.target.value)} placeholder="Es. Società Calcio Bologna" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Categoria / tipologia</Label>
                  <Input data-testid="import-categoria-input" value={impCat} onChange={(e) => setImpCat(e.target.value)} placeholder="Es. Calcio Bologna, Fiera Settembre…" list="cat-suggest" />
                  <datalist id="cat-suggest">{categories.map((c) => <option key={c} value={c} />)}</datalist>
                </div>
                <div className="grid gap-1.5">
                  <Label>Note (facoltative)</Label>
                  <Textarea data-testid="import-note-input" value={impNote} onChange={(e) => setImpNote(e.target.value)} rows={2} placeholder="Provenienza della lista, contesto…" />
                </div>
                <div className="grid gap-1.5">
                  <Label>File Excel / CSV *</Label>
                  <input ref={fileRef} data-testid="import-file-input" type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setImpFile(e.target.files?.[0] || null)}
                    className="text-sm file:mr-3 file:rounded-full file:border-0 file:bg-primary file:text-white file:px-4 file:py-2 file:text-sm file:font-medium hover:file:bg-primary/90 cursor-pointer" />
                  {impFile && <span className="text-xs text-slate-400 inline-flex items-center gap-1"><FileSpreadsheet className="h-3 w-3" />{impFile.name}</span>}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setImpOpen(false)} className="rounded-full">Annulla</Button>
                <Button data-testid="import-confirm-button" onClick={doImport} disabled={importing} className="rounded-full gap-1.5">
                  <Upload className="h-4 w-4" /> {importing ? "Importazione…" : "Importa"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {categories.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-400 inline-flex items-center gap-1"><Filter className="h-3.5 w-3.5" /> Categoria:</span>
          <button data-testid="cat-filter-all" onClick={() => setCatFilter("all")} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${catFilter === "all" ? "bg-primary text-white border-primary" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>Tutte</button>
          {categories.map((c) => (
            <button key={c} data-testid={`cat-filter-${c}`} onClick={() => setCatFilter(c)} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${catFilter === c ? "bg-primary text-white border-primary" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>{c}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="p-10 text-slate-400">Caricamento…</div>
      ) : visibleListe.length === 0 ? (
        <Card className="p-12 text-center">
          <ClipboardList className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="text-slate-500 font-medium">Nessuna lista ancora.</div>
          <p className="text-sm text-slate-400 mt-1">Scarica il modello Excel, compilalo con le società da contattare e importalo.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visibleListe.map((l) => (
            <Card key={l.id} data-testid={`lista-card-${l.id}`} onClick={() => openLista(l)} className="p-5 cursor-pointer hover:shadow-md hover:border-primary/40 transition-all animate-fade-up">
              <div className="flex items-start justify-between gap-2">
                <div className="font-bold text-slate-900 dark:text-white leading-tight">{l.nome}</div>
                <Badge variant="outline" className="gap-1 shrink-0"><Layers className="h-3 w-3" />{l.categoria}</Badge>
              </div>
              {l.note && <p className="text-xs text-slate-400 mt-1.5 line-clamp-2">{l.note}</p>}
              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 p-2.5 text-center">
                  <div className="text-lg font-extrabold text-slate-900 dark:text-white tabular-nums">{l.totale}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Totale</div>
                </div>
                <div className="rounded-lg bg-sky-50 dark:bg-sky-500/10 p-2.5 text-center">
                  <div className="text-lg font-extrabold text-sky-600 tabular-nums">{l.da_contattare}</div>
                  <div className="text-[10px] uppercase tracking-wider text-sky-600/70">Da fare</div>
                </div>
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-500/10 p-2.5 text-center">
                  <div className="text-lg font-extrabold text-emerald-600 tabular-nums">{l.spostati}</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-600/70">Pipeline</div>
                </div>
              </div>
              {l.eliminati > 0 && <div data-testid={`lista-eliminati-${l.id}`} className="mt-2 text-xs font-semibold text-rose-600">{l.eliminati} eliminate da Pipeline</div>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
